import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Body } from '@sumup-oss/circuit-ui';
import type { Entry } from 'contentful';
import { useRouter } from 'next/router';
import React, { FC, useCallback, useMemo } from 'react';

import DTSBanner from '../DTSBanner';
import EscalationOptions from '../EscalationOptions';

import { PageContentTitle } from 'components/styledComponents';
import { RICH_TEXT_RENDERER_OPTIONS } from 'constants/richTextRendererOptions';
import { shouldShowDTSBanner } from 'services/pages/articlesPage';
import { getCountryFromLocale } from 'src/services/localization';
import type {
  ContentfulArticle,
  ContentfulCountry,
  IDSEscalationOption,
} from 'types/common';

type ArticleProps = {
  articleData?: ContentfulArticle;
  chatAvailable: boolean;
  countriesData?: Entry<ContentfulCountry>[];
  escalationOptions?: IDSEscalationOption[];
  isPos?: boolean;
};

const Article: FC<ArticleProps> = ({
  articleData,
  chatAvailable,
  countriesData,
  escalationOptions,
  isPos = false,
}) => {
  const router = useRouter();
  const countryCode = getCountryFromLocale(router.locale);

  const currentCountryDetails = useMemo(
    () =>
      countriesData &&
      Object.values(countriesData).find(
        (country) => country?.fields?.twoLetterIsoCode === countryCode,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [countriesData, countryCode],
  );

  const escalationType = currentCountryDetails?.fields?.escalationOptions[0];
  const emailAddress = currentCountryDetails?.fields?.emailAddress;
  const BottomComponent = useCallback(() => {
    if (isPos) {
      return null;
    }
    if (shouldShowDTSBanner(articleData?.sys?.id, router?.locale)) {
      return <DTSBanner />;
    }
    return <EscalationOptions />;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    articleData?.fields?.IDSDetailedReason,
    articleData?.fields.title,
    articleData?.sys.id,
    chatAvailable,
    emailAddress,
    escalationOptions,
    escalationType,
    isPos,
    router?.locale,
  ]);

  return articleData?.fields ? (
    <div>
      <PageContentTitle as="h1" size="l">
        {articleData.fields.title}
      </PageContentTitle>
      <Body size="m">{articleData.fields.articleDescription}</Body>
      <div className="article-body">
        {documentToReactComponents(
          articleData.fields.scArticleBody,
          RICH_TEXT_RENDERER_OPTIONS,
        )}
      </div>

      {shouldShowDTSBanner(articleData?.sys?.id, router?.locale) ? (
        <DTSBanner />
      ) : (
        <BottomComponent />
      )}
    </div>
  ) : null;
};

export default Article;
